import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { scheduleAPI, profileAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap,
  Home,
  User,
  BarChart3,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  Calendar,
  Clock,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  UserPlus,
  ShoppingBag,
  ClipboardList
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const PROFILE_COLORS = {
  green: { bg: 'bg-emerald-500', light: 'bg-emerald-100 text-emerald-700' },
  blue: { bg: 'bg-blue-500', light: 'bg-blue-100 text-blue-700' },
  red: { bg: 'bg-red-500', light: 'bg-red-100 text-red-700' },
  yellow: { bg: 'bg-yellow-500', light: 'bg-yellow-100 text-yellow-700' },
  purple: { bg: 'bg-purple-500', light: 'bg-purple-100 text-purple-700' },
  black: { bg: 'bg-slate-900', light: 'bg-slate-200 text-slate-700' },
};

const SchedulingPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    profile_id: '',
    day_of_week: 0,
    start_time: '09:00',
    end_time: '17:00',
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, profilesRes, activeRes] = await Promise.all([
        scheduleAPI.list(),
        profileAPI.list(),
        scheduleAPI.getActiveProfile(),
      ]);
      setSchedules(schedulesRes.data);
      setProfiles(profilesRes.data);
      setActiveProfile(activeRes.data);
    } catch (error) {
      toast.error('Failed to load scheduling data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const validateForm = () => {
    if (!formData.profile_id) {
      setFormError('Please select a profile');
      return false;
    }
    if (formData.start_time >= formData.end_time) {
      setFormError('End time must be after start time');
      return false;
    }
    
    // Check for overlapping schedules
    const overlap = schedules.find(s => 
      s.day_of_week === formData.day_of_week &&
      !(formData.end_time <= s.start_time || formData.start_time >= s.end_time)
    );
    if (overlap) {
      setFormError(`Schedule overlaps with existing schedule on ${DAYS_OF_WEEK[overlap.day_of_week].label}`);
      return false;
    }
    
    setFormError('');
    return true;
  };

  const handleCreateSchedule = async () => {
    if (!validateForm()) return;
    
    try {
      const response = await scheduleAPI.create(formData);
      setSchedules([...schedules, response.data]);
      setShowForm(false);
      setFormData({ profile_id: '', day_of_week: 0, start_time: '09:00', end_time: '17:00' });
      toast.success('Schedule created!');
      // Refresh active profile
      const activeRes = await scheduleAPI.getActiveProfile();
      setActiveProfile(activeRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Delete this schedule?')) return;
    
    try {
      await scheduleAPI.delete(scheduleId);
      setSchedules(schedules.filter(s => s.schedule_id !== scheduleId));
      toast.success('Schedule deleted');
      // Refresh active profile
      const activeRes = await scheduleAPI.getActiveProfile();
      setActiveProfile(activeRes.data);
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling', active: true },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers' },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Profile Scheduling
            </h1>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-schedule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Schedule
              </Button>
            </div>
          </div>
        </header>

        <main className="p-6" data-testid="scheduling-page">
          {/* Active Profile Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Currently Active Profile
              </h3>
            </div>
            
            {activeProfile?.scheduled && activeProfile.profile ? (
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${PROFILE_COLORS[activeProfile.profile.color]?.bg || 'bg-indigo-500'} rounded-xl flex items-center justify-center`}>
                  <span className="text-white font-bold">
                    {activeProfile.profile.profile_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{activeProfile.profile.profile_name}</p>
                  <p className="text-sm text-slate-500">
                    Active until {activeProfile.schedule?.end_time} • {DAYS_OF_WEEK[activeProfile.schedule?.day_of_week]?.label}
                  </p>
                </div>
                <div className="ml-auto">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Active
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-slate-500">
                <AlertCircle className="w-5 h-5" />
                <p>No scheduled profile active right now. Default profile will be used.</p>
              </div>
            )}
          </div>

          {/* Create Schedule Form */}
          {showForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="schedule-form">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Create New Schedule
              </h3>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label>Profile</Label>
                  <Select
                    value={formData.profile_id}
                    onValueChange={(value) => setFormData({ ...formData, profile_id: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="profile-select">
                      <SelectValue placeholder="Select profile" />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles.map((profile) => (
                        <SelectItem key={profile.profile_id} value={profile.profile_id}>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded ${PROFILE_COLORS[profile.color]?.bg || 'bg-slate-500'}`} />
                            {profile.profile_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Day of Week</Label>
                  <Select
                    value={formData.day_of_week.toString()}
                    onValueChange={(value) => setFormData({ ...formData, day_of_week: parseInt(value) })}
                  >
                    <SelectTrigger className="mt-1" data-testid="day-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((day) => (
                        <SelectItem key={day.value} value={day.value.toString()}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    className="mt-1"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    data-testid="start-time-input"
                  />
                </div>
                
                <div>
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    className="mt-1"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    data-testid="end-time-input"
                  />
                </div>
              </div>
              
              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}
              
              <div className="flex gap-3">
                <Button onClick={handleCreateSchedule} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-schedule-btn">
                  Create Schedule
                </Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setFormError(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Schedules Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Schedules ({schedules.length})
              </h3>
            </div>
            
            {schedules.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No schedules yet</p>
                <p className="text-sm text-slate-400">Create a schedule to auto-switch profiles by time</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Day</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Profile</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {schedules.sort((a, b) => a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time)).map((schedule) => {
                      const profile = profiles.find(p => p.profile_id === schedule.profile_id);
                      return (
                        <tr key={schedule.schedule_id} className="hover:bg-slate-50" data-testid={`schedule-row-${schedule.schedule_id}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-medium text-slate-900">
                              {DAYS_OF_WEEK[schedule.day_of_week]?.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-slate-600">
                              {schedule.start_time} - {schedule.end_time}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {profile ? (
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 ${PROFILE_COLORS[profile.color]?.bg || 'bg-slate-500'} rounded`} />
                                <span className="font-medium text-slate-900">{profile.profile_name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${PROFILE_COLORS[profile.color]?.light || 'bg-slate-100 text-slate-600'}`}>
                                  {profile.color}
                                </span>
                              </div>
                            ) : (
                              <span className="text-slate-400">Profile not found</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-schedule-${schedule.schedule_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Info Card */}
          <div className="mt-6 bg-indigo-50 rounded-xl border border-indigo-200 p-6">
            <h4 className="font-semibold text-indigo-900 mb-2">How Scheduling Works</h4>
            <ul className="text-sm text-indigo-700 space-y-1">
              <li>• Create schedules to auto-activate profiles at specific days/times</li>
              <li>• Perfect for BNI meetings (e.g., activate "BNI Profile" every Tuesday 7-9am)</li>
              <li>• Cube QR codes will route to the currently active scheduled profile</li>
              <li>• If no schedule is active, your default profile will be used</li>
            </ul>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SchedulingPage;
